// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { useSuiClient } from '@mysten/dapp-kit';
import { ChevronRight12 } from '@mysten/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { getCpcAssetEntries, type CpcAssetEntry } from '~/config/cpcAssets.config';

type VerifyState = 'loading' | 'on-chain' | 'missing' | 'n/a';

function statusForEntry(
	entry: CpcAssetEntry,
	objectQ: { isPending: boolean; isError: boolean; isSuccess: boolean; isFetching: boolean },
	coinQ: { isPending: boolean; isError: boolean; isSuccess: boolean; isFetching: boolean },
): VerifyState {
	if (entry.objectId) {
		if (objectQ.isPending || objectQ.isFetching) return 'loading';
		if (objectQ.isSuccess) return 'on-chain';
		if (objectQ.isError) return 'missing';
		return 'loading';
	}
	if (entry.typeTag) {
		if (coinQ.isPending || coinQ.isFetching) return 'loading';
		if (coinQ.isSuccess) return 'on-chain';
		if (coinQ.isError) return 'missing';
		return 'loading';
	}
	return 'n/a';
}

function StatusBadge({ state, compact }: { state: VerifyState; compact?: boolean }) {
	const config = {
		'on-chain': { label: 'ON CHAIN', color: '#0ECB81' },
		missing: { label: 'MISSING', color: '#F6465D' },
		loading: { label: 'SYNCHING', color: '#F0B90B' },
		'n/a': { label: '—', color: '#848E9C' },
	}[state];

	return (
		<div className={clsx('flex items-center gap-1.5', compact ? 'w-auto' : 'w-[80px]')}>
			<div className="h-1.5 w-1.5 rounded-full" style={{ background: config.color }} />
			<span className="text-[10px] font-semibold uppercase" style={{ color: config.color }}>
				{config.label}
			</span>
		</div>
	);
}

function MobileAssetCard({
	entry,
	state,
	detail,
	href,
	variant,
}: {
	entry: CpcAssetEntry;
	state: VerifyState;
	detail: string;
	href?: string;
	variant: 'home' | 'trade';
}) {
	const [isOpen, setIsOpen] = useState(false);
	const borderCls =
		variant === 'trade'
			? isOpen
				? 'border-[#38BDF8]/40 bg-[#0b1220]'
				: 'border-white/10 bg-[#0b1220]/60'
			: isOpen
				? 'border-[#4DB7FF]/40 bg-[#0E1A33]'
				: 'border-[#1A2744] bg-[#0E1A33]/60';

	return (
		<div className={clsx('mb-2 overflow-hidden rounded-lg border transition-all', borderCls)}>
			<button
				type="button"
				className="flex w-full cursor-pointer items-center justify-between gap-3 p-3 text-left select-none active:bg-white/5"
				onClick={() => setIsOpen(!isOpen)}
			>
				<span className="truncate text-sm font-semibold text-[#EAECEF]">
					{entry.symbol ?? entry.label}
				</span>
				<div className="flex shrink-0 items-center gap-2">
					<StatusBadge state={state} compact />
					<ChevronRight12
						className={clsx('h-3 w-3 text-[#848E9C] transition-transform', isOpen && 'rotate-90')}
					/>
				</div>
			</button>
			{isOpen ? (
				<div className="space-y-2 border-t border-white/10 bg-black/20 p-3 text-xs">
					<p className="text-[#848E9C]">{entry.description ?? entry.label}</p>
					<p className="break-all font-mono text-[#848E9C]" title={detail}>
						{detail}
					</p>
					{href ? (
						<Link to={href} className="inline-flex items-center gap-1 font-semibold text-[#38BDF8]">
							View <ChevronRight12 className="h-2.5 w-2.5" />
						</Link>
					) : null}
				</div>
			) : null}
		</div>
	);
}

function PCAssetRow({
	entry,
	state,
	detail,
	href,
}: {
	entry: CpcAssetEntry;
	state: VerifyState;
	detail: string;
	href?: string;
}) {
	return (
		<tr className="border-b border-white/10 transition-colors hover:bg-white/[0.03]">
			<td className="px-2 py-2 text-sm font-medium whitespace-nowrap text-[#EAECEF]">
				{entry.symbol ?? entry.label}
			</td>
			<td className="px-2 py-2 text-xs text-[#848E9C]">{entry.description ?? entry.label}</td>
			<td className="max-w-[180px] truncate px-2 py-2 font-mono text-[10px] text-[#848E9C]" title={detail}>
				{detail.slice(0, 6)}…{detail.slice(-12)}
			</td>
			<td className="px-2 py-2">
				<StatusBadge state={state} compact />
			</td>
			<td className="px-2 py-2 text-right">
				{href ? (
					<Link to={href} className="text-xs font-medium text-[#38BDF8] hover:text-[#7DD3FC]">
						View
					</Link>
				) : (
					<span className="text-[#5E6673]">—</span>
				)}
			</td>
		</tr>
	);
}

function AssetDataGate({
	entry,
	isMobile,
	variant,
}: {
	entry: CpcAssetEntry;
	isMobile: boolean;
	variant: 'home' | 'trade';
}) {
	const client = useSuiClient();
	const objectQ = useQuery({
		queryKey: ['cpc-asset-object', entry.objectId],
		enabled: !!entry.objectId,
		queryFn: async () => {
			const res = await client.getObject({
				id: entry.objectId!,
				options: { showType: true, showOwner: true },
			});
			if (res.error || !res.data) throw new Error('not found');
			return res.data;
		},
		retry: 1,
		staleTime: 30_000,
	});
	const coinQ = useQuery({
		queryKey: ['cpc-asset-coin', entry.typeTag],
		enabled: !!entry.typeTag && !entry.objectId,
		queryFn: async () => client.getCoinMetadata({ coinType: entry.typeTag! }),
		retry: 1,
		staleTime: 30_000,
	});

	const state = statusForEntry(entry, objectQ, coinQ);
	const href = entry.objectId ? `/object/${entry.objectId}` : undefined;
	const detail = entry.typeTag ?? entry.objectId ?? '—';

	if (isMobile) {
		return <MobileAssetCard entry={entry} state={state} detail={detail} href={href} variant={variant} />;
	}
	return <PCAssetRow entry={entry} state={state} detail={detail} href={href} />;
}

export function CpcAssetsList({
	variant = 'trade',
	entries: entriesProp,
}: {
	variant?: 'home' | 'trade';
	entries?: CpcAssetEntry[];
}) {
	const entries = entriesProp ?? getCpcAssetEntries();
	if (entries.length === 0) {
		return (
			<p className="py-4 text-center text-sm text-[#848E9C]">No assets configured</p>
		);
	}

	const tableHeadCls =
		variant === 'trade'
			? 'bg-[#0b1220] text-[10px] font-bold uppercase tracking-wider text-[#848E9C]'
			: 'bg-[#101C38] text-xs font-bold uppercase tracking-wider text-[#AEB9D0]';

	return (
		<>
			<div className="hidden md:block overflow-x-auto rounded-lg border border-white/10">
				<table className="w-full min-w-[640px]">
					<thead>
						<tr>
							<th className={clsx('px-2 py-2 text-left', tableHeadCls)}>SYMBOL</th>
							<th className={clsx('px-2 py-2 text-left', tableHeadCls)}>DESCRIPTION</th>
							<th className={clsx('px-2 py-2 text-left', tableHeadCls)}>TYPE / OBJECT</th>
							<th className={clsx('px-2 py-2 text-left', tableHeadCls)}>RPC</th>
							<th className={clsx('px-2 py-2 text-right', tableHeadCls)}>LINK</th>
						</tr>
					</thead>
					<tbody>
						{entries.map((e) => (
							<AssetDataGate key={e.key} entry={e} isMobile={false} variant={variant} />
						))}
					</tbody>
				</table>
			</div>
			<div className="md:hidden">
				{entries.map((e) => (
					<AssetDataGate key={e.key} entry={e} isMobile variant={variant} />
				))}
			</div>
		</>
	);
}
